import React, { Suspense, useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import { setPodName } from '@helpers/Axios';
import SuspenseFallback from '@components/common/SuspenseFallback';
import CustomDomainVO from '@interfaces/CustomDomainVO';
import { lazyLoadWithRetry } from '@components/common/RfpLazyWithRetry';

const OtpModal = lazyLoadWithRetry(() => import('@components/artifacts/gating/modals/OtpModal'), 'OtpModal');
import { useGlobalStoreActions, useGlobalStoreState } from '@store/global/GlobalStore';
import { ERROR_DETAILS_MAP, WARNING_ERROR_LIST } from '@constants/ErrorConstants';
import { updatePageMetadata, getCompanyInfo, getDomainDetails, populateGuestProps } from '@service/UserLoadService';
import IRouteProps from '@interfaces/RouteProps';
import { resetI18 } from '../i18n';

type RouteComponent = IRouteProps['component'];

const WarningPage = lazyLoadWithRetry(() => import('../WarningPage'), 'WarningPage');
const WarningModal = lazyLoadWithRetry(() => import('../WarningModal'), 'WarningModal');
const LoginPage = lazyLoadWithRetry(() => import('./login/Login'), 'LoginPage');

const LoginWrapper = ({ setValidated }: { setValidated: (validated: boolean) => void }) => {
  const guestKey = useGlobalStoreState(state => state.general.guestProps.guestKey);

  return (
    <Suspense fallback={<SuspenseFallback fullscreen />}>
      <LoginPage guestKey={guestKey} setValidated={setValidated} />
    </Suspense>
  );
};

const DynamicLoader = ({ component }: { component: RouteComponent }) => {
  const LazyComponent = React.useMemo(() => component, [component]);

  return (
    <Suspense fallback={<SuspenseFallback fullscreen />}>
      <LazyComponent />
    </Suspense>
  );
};

const ComponentWrapper = React.memo(({ component }: { component: RouteComponent }) => {
  const [initLoaded, setInitLoaded] = useState<boolean>(false);
  const fetchInitialData = useGlobalStoreActions(actions => actions.general.fetchInitialData);
  const reloadInitialData = useGlobalStoreState(state => state.general.reloadInitialData);
  const setReloadInitialData = useGlobalStoreActions(actions => actions.general.setReloadInitialData);

  const fetchdata = () => {
    fetchInitialData({
      onLoaded: () => {
        setInitLoaded(true);
        setReloadInitialData(false);
      }
    });
  };

  useEffect(() => {
    if (reloadInitialData) {
      fetchdata();
    }
  }, [reloadInitialData]);

  useEffect(() => {
    fetchdata();
  }, []);

  if (!initLoaded) {
    return <SuspenseFallback fullscreen />;
  }

  return <DynamicLoader component={component} />;
});

const BaseLayout = ({ children }: { children: RouteComponent }) => {
  const { companyId: urlCompanyId, profileId: urlProfileId, guestKey: urlGuestKey } = useParams();
  const isPublicUser: boolean = !window.location.href.includes('/profile-view');
  const initialLoaded = useGlobalStoreState(state => state.general.guestProps.loaded);
  const [pageState, setPageState] = useState({ loaded: initialLoaded, isError: false, validated: isPublicUser });
  const [openValidateOtp, setOpenValidateOtp] = useState(false);

  const errorKey = useGlobalStoreState(state => state.general.errorKey);
  const setErrorKey = useGlobalStoreActions(actions => actions.general.setErrorKey);
  const setGuestProps = useGlobalStoreActions(actions => actions.general.setGuestProps);
  const setRequestAccess = useGlobalStoreActions(actions => actions.general.setRequestAccess);
  const setOtpVerified = useGlobalStoreActions(actions => actions.general.setOtpVerified);

  const actions = { setGuestProps, setRequestAccess, setOtpVerified, setOpenValidateOtp };

  useEffect(() => {
    if (initialLoaded) {
      return;
    }
    if (isPublicUser) {
      getCompanyInfo(
        (companyId: string, podName: string) => {
          setPodName(podName);
          resetI18(podName);
          getDomainDetails(
            companyId,
            podName,
            (customDomainVO: CustomDomainVO, detailsPodName: string) => {
              if (!urlProfileId || customDomainVO.entityId === urlProfileId) {
                const url = customDomainVO.favicon
                  ? `${window.constants.baseUrl}guest_v3/${customDomainVO.companyId}/images/${customDomainVO.favicon}?podName=${detailsPodName}`
                  : 'https://app.rfpio.com/favicon.ico';
                updatePageMetadata(customDomainVO.title || 'Trust Center', url);
                populateGuestProps(setPageState, setErrorKey, actions, {
                  isPublicUser,
                  urlCompanyId,
                  urlProfileId,
                  urlGuestKey,
                  customDomainVO,
                  domainPodName: podName
                });
              } else {
                populateGuestProps(setPageState, setErrorKey, actions, { isPublicUser, urlCompanyId, urlProfileId, urlGuestKey });
              }
              if (window.location.href.includes('profile-center')) {
                const newurl = window.location.origin;
                window.history.replaceState({ path: newurl }, '', newurl);
              }
            },
            () => {
              setPageState(prev => ({ ...prev, loaded: true, isError: true }));
            }
          );
        },
        (status?: number) => {
          if (status === 204) {
            // set icon and title when no custom domain exists
            const url = 'https://app.rfpio.com/favicon.ico';
            updatePageMetadata('Trust Center', url);
            populateGuestProps(setPageState, setErrorKey, actions, { isPublicUser, urlCompanyId, urlProfileId, urlGuestKey });
          } else {
            setPageState(prev => ({ ...prev, loaded: true, isError: true }));
          }
        }
      );
    } else {
      const url = 'https://app.rfpio.com/favicon.ico';
      updatePageMetadata('Trust Center', url);
      populateGuestProps(setPageState, setErrorKey, actions, { isPublicUser, urlCompanyId, urlProfileId, urlGuestKey });
    }
  }, [initialLoaded]);

  if (!pageState.loaded) {
    return isPublicUser ? <SuspenseFallback fullscreen /> : <></>;
  }

  if (errorKey && ERROR_DETAILS_MAP[errorKey]) {
    return (
      <Suspense fallback={<></>}>
        <WarningPage />
      </Suspense>
    );
  }

  if (!pageState.validated) {
    return <LoginWrapper setValidated={(validated: boolean) => setPageState(prev => ({ ...prev, validated }))} />;
  }

  return (
    <>
      <ComponentWrapper component={children} />
      {openValidateOtp && (
        <Suspense fallback={<></>}>
          <OtpModal setOpenValidateOTP={setOpenValidateOtp} needRerender />
        </Suspense>
      )}
      {errorKey && WARNING_ERROR_LIST.includes(errorKey) && (
        <Suspense fallback={<></>}>
          <WarningModal action={errorKey} />
        </Suspense>
      )}
    </>
  );
};

export default BaseLayout;
